import { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const GRADES = [
  "LKG",
  "UKG",
  "I Class",
  "II Class",
  "III Class",
  "IV Class",
  "V Class",
  "VI Class",
  "VII Class"
];

const SUBJECTS = ["Maths", "English", "Science"];

const QUESTIONS = {
  Maths: [
    { q: "2 + 3 =", a: "5" },
    { q: "12 + 18 =", a: "30" },
    { q: "8 - 2 =", a: "6" },
    { q: "25 - 18 =", a: "7" }
  ],
  English: [
    { q: "Complete: A B _ D", a: "C" },
    { q: "Small letter of 'A'", a: "a" },
    { q: "Fill: C _ T", a: "A" }
  ],
  Science: [
    { q: "The sun is a:", a: "star" },
    { q: "We breathe using:", a: "lungs" },
    { q: "Water turns to ice when it:", a: "freezes" }
  ]
};

const normalize = (v) => String(v || "").trim().toLowerCase();

function OnlinePractice(){
  const [step, setStep] = useState("grade");
  const [grade, setGrade] = useState(null);
  const [subject, setSubject] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const items = useMemo(() => {
    if (!subject) return [];
    return QUESTIONS[subject] || [];
  }, [subject]);

  const onChange = (idx, value) => {
    setAnswers((prev) => ({
      ...prev,
      [`${grade}-${subject}-${idx}`]: value
    }));
  };

  const check = (idx, correct) => {
    if (!submitted) return null;
    const key = `${grade}-${subject}-${idx}`;
    const val = answers[key] || "";
    if (!val) return null;
    return normalize(val) === normalize(correct);
  };

  return (
    <div
      className="op-shell"
      style={{
        minHeight: "100vh",
        padding: "28px",
        background: "linear-gradient(145deg, #FFE9B6 0%, #E6F2FF 50%, #FBE3FF 100%)",
        backgroundImage: "radial-gradient(circle at 8% 10%, rgba(255,255,255,0.9) 0, rgba(255,255,255,0) 45%), radial-gradient(circle at 90% 12%, rgba(255,244,220,0.9) 0, rgba(255,244,220,0) 40%)",
        fontFamily: '"Baloo 2", "Comic Sans MS", "Trebuchet MS", sans-serif'
      }}
    >
      <style>{`
        .op-shell { box-sizing: border-box; }
        .op-container { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1fr; gap: 18px; }
        .op-nav { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
        .op-hero { padding: 22px; }
        .op-grade-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; }
        .op-practice-grid { display: grid; gap: 14px; }
        .op-bottom-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px; }
        @media (max-width: 900px) {
          .op-shell { padding: 18px; }
          .op-hero { padding: 18px; }
        }
        @media (max-width: 640px) {
          .op-shell { padding: 14px; }
          .op-hero { padding: 16px; }
          .op-grade-grid { grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); }
        }
      `}</style>
      <Helmet>
        <title>Online Practice – PracticeNest</title>
        <meta
          name="description"
          content="Solve worksheets online with instant feedback. Practice Maths, English, Science and more."
        />
        <link rel="canonical" href="https://practicenest.in/practice" />
      </Helmet>

      <div className="op-container">
        <div className="op-nav">
          <Link to="/" style={{ textDecoration: "none", fontWeight: "700" }}>← Home</Link>
          <Link to="/worksheets" style={{ textDecoration: "none", fontWeight: "700" }}>Worksheets</Link>
        </div>

        <div
          className="op-hero"
          style={{
            position: "relative",
            background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBFF 100%)",
            borderRadius: "22px",
            boxShadow: "0 18px 34px rgba(0,0,0,0.1)",
            border: "1px solid rgba(0,0,0,0.06)"
          }}
        >
          <div style={{ fontSize: "12px", letterSpacing: "1px", textTransform: "uppercase", color: "#3A6EA5" }}>PracticeNest</div>
          <h1 style={{ marginTop: "6px", marginBottom: "6px", fontSize: "32px" }}>Online Practice</h1>
          <p style={{ marginTop: 0 }}>Choose grade, then subject, and solve online.</p>

          {step === "grade" && (
            <div className="op-grade-grid" style={{ marginTop: "10px" }}>
              {GRADES.map((g) => (
                <button
                  key={g}
                  onClick={() => {
                    setGrade(g);
                    setStep("subject");
                  }}
                  style={{
                    padding: "12px",
                    borderRadius: "14px",
                    border: "2px solid #FFD34E",
                    background: "linear-gradient(135deg, #FFF8D6 0%, #FFFFFF 100%)",
                    cursor: "pointer",
                    fontWeight: "800"
                  }}
                >
                  {g}
                </button>
              ))}
            </div>
          )}

          {step === "subject" && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px" }}>
                <div style={{ fontWeight: "700" }}>Grade: {grade}</div>
                <button
                  onClick={() => {
                    setStep("grade");
                    setSubject(null);
                    setSubmitted(false);
                  }}
                  style={{
                    padding: "6px 10px",
                    borderRadius: "10px",
                    border: "1px solid #bbb",
                    background: "#fff",
                    cursor: "pointer"
                  }}
                >
                  Change Grade
                </button>
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "12px" }}>
                {SUBJECTS.map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setSubject(s);
                      setStep("practice");
                      setSubmitted(false);
                    }}
                    style={{
                      padding: "10px 14px",
                      borderRadius: "999px",
                      border: "2px solid #111",
                      background: "linear-gradient(135deg, #B8FFCB 0%, #E9FFE5 100%)",
                      cursor: "pointer",
                      fontWeight: "800"
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}

          {step === "practice" && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px" }}>
              <div style={{ fontWeight: "700" }}>{grade} • {subject}</div>
              <button
                onClick={() => setStep("subject")}
                style={{
                  padding: "6px 10px",
                  borderRadius: "10px",
                  border: "1px solid #bbb",
                  background: "#fff",
                  cursor: "pointer"
                }}
              >
                Change Subject
              </button>
            </div>
          )}
        </div>

        {step === "practice" && (
          <div className="op-practice-grid" style={{ marginTop: "20px" }}>
            {items.map((item, idx) => {
              const result = check(idx, item.a);
              return (
                <div
                  key={`${grade}-${subject}-${idx}`}
                  style={{
                    background: "#fff",
                    borderRadius: "16px",
                    padding: "16px",
                    boxShadow: "0 10px 22px rgba(0,0,0,0.08)",
                    borderLeft: result === true ? "6px solid #4CAF50" : result === false ? "6px solid #EF5350" : "6px solid transparent"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: "18px", fontWeight: "700" }}>{item.q}</div>
                    <div style={{ fontSize: "18px", fontWeight: "800" }}>
                      {result === true ? "✓" : result === false ? "✗" : ""}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={answers[`${grade}-${subject}-${idx}`] || ""}
                    onChange={(e) => onChange(idx, e.target.value)}
                    placeholder="Type your answer"
                    style={{
                      marginTop: "10px",
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "10px",
                      border: "1px solid #ddd",
                      fontSize: "16px"
                    }}
                  />
                  {submitted && result === false && (
                    <div style={{ color: "#EF5350", marginTop: "6px", fontWeight: "600" }}>
                      Try again
                    </div>
                  )}
                  {submitted && result === true && (
                    <div style={{ color: "#2E7D32", marginTop: "6px", fontWeight: "600" }}>
                      Correct
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setSubmitted(true)}
                style={{
                  padding: "10px 16px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#2D7FF9",
                  color: "#fff",
                  fontWeight: "800",
                  cursor: "pointer"
                }}
              >
                Submit Answers
              </button>
              {submitted && (
                <button
                  onClick={() => setSubmitted(false)}
                  style={{
                    padding: "10px 16px",
                    borderRadius: "10px",
                    border: "1px solid #bbb",
                    background: "#fff",
                    fontWeight: "800",
                    cursor: "pointer"
                  }}
                >
                  Edit Answers
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {step === "practice" && (
        <div className="op-bottom-grid" style={{ maxWidth: "1200px", margin: "18px auto 0" }}>
          <div style={{ background: "#fff", borderRadius: "16px", padding: "14px", boxShadow: "0 10px 22px rgba(0,0,0,0.08)" }}>
            <div style={{ fontWeight: "800", marginBottom: "6px" }}>Today’s Tip</div>
            <div style={{ fontSize: "14px", color: "#555" }}>Try 10 minutes daily for quick improvement.</div>
          </div>
          <div style={{ background: "#fff", borderRadius: "16px", padding: "14px", boxShadow: "0 10px 22px rgba(0,0,0,0.08)" }}>
            <div style={{ fontWeight: "800", marginBottom: "6px" }}>Stars Earned</div>
            <div style={{ fontSize: "20px" }}>⭐ ⭐ ⭐</div>
          </div>
          <div style={{ background: "#fff", borderRadius: "16px", padding: "14px", boxShadow: "0 10px 22px rgba(0,0,0,0.08)" }}>
            <div style={{ fontWeight: "800", marginBottom: "6px" }}>Weekly Goal</div>
            <div style={{ fontSize: "14px", color: "#555" }}>Complete 3 subjects this week.</div>
          </div>
          <div style={{ background: "#fff", borderRadius: "16px", padding: "14px", boxShadow: "0 10px 22px rgba(0,0,0,0.08)" }}>
            <div style={{ fontWeight: "800", marginBottom: "6px" }}>Fun Facts</div>
            <div style={{ fontSize: "14px", color: "#555" }}>Did you know? Practice makes learning easier.</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OnlinePractice;
